// Payment provider feature flag.
// =================================
// Resolución (precedencia, mayor → menor):
//   1. ?provider=onvopay|stripe en la URL  (override de sesión)
//   2. localStorage "payment_provider"      (selección persistente del usuario)
//   3. VITE_PAYMENT_PROVIDER (env)
//   4. Default: "stripe"
//
// Stripe → Embedded Checkout (clientSecret + EmbeddedCheckoutProvider).
// ONVO   → Redirect a `session.url` (hosted checkout).

export type PaymentProvider = "stripe" | "onvopay";

const STORAGE_KEY = "payment_provider";

function readEnv(): PaymentProvider {
  const fromEnv = (import.meta.env.VITE_PAYMENT_PROVIDER as string | undefined)?.toLowerCase();
  if (fromEnv === "onvopay") return "onvopay";
  return "stripe";
}

function readQuery(): PaymentProvider | null {
  if (typeof window === "undefined") return null;
  const q = new URLSearchParams(window.location.search).get("provider");
  if (q === "onvopay" || q === "stripe") return q;
  return null;
}

function readStorage(): PaymentProvider | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "onvopay" || v === "stripe") return v;
  } catch {
    /* SSR / privacy mode */
  }
  return null;
}

/**
 * Resuelve el proveedor de pago activo.
 * Override: query > localStorage > env > default ("stripe").
 */
// Stripe deshabilitado temporalmente (sandbox vencido). Poner en false para reactivarlo.
export const STRIPE_DISABLED = true;

export function getPaymentProvider(): PaymentProvider {
  if (STRIPE_DISABLED) return "onvopay";
  return readQuery() ?? readStorage() ?? readEnv();
}

/** Persiste la selección y emite un evento para que la UI re-renderice. */
export function setPaymentProvider(provider: PaymentProvider): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, provider);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent("payment-provider-changed", { detail: provider }));
}

/** Preserva el provider activo en navegación cliente cuando viene por query string. */
export function getPaymentProviderSearch(): { provider: PaymentProvider } | undefined {
  const provider = readQuery();
  return provider ? { provider } : undefined;
}

/** Mapeo de scope (primary/zone/resale) a edge function name según provider. */
export function getCheckoutFunctionName(
  scope: "ticket" | "zone" | "resale",
  provider: PaymentProvider = getPaymentProvider(),
): string {
  if (provider === "onvopay") {
    return scope === "ticket"
      ? "create-ticket-checkout-onvo"
      : scope === "zone"
        ? "create-zone-checkout-onvo"
        : "create-resale-checkout-onvo";
  }
  return scope === "ticket"
    ? "create-ticket-checkout"
    : scope === "zone"
      ? "create-zone-checkout"
      : "create-resale-checkout";
}

/** Log estandarizado del provider usado al iniciar checkout. Visible en consola del navegador. */
export function logCheckoutProvider(scope: "ticket" | "zone" | "resale"): PaymentProvider {
  const provider = getPaymentProvider();
  const fn = getCheckoutFunctionName(scope, provider);
  // eslint-disable-next-line no-console
  console.info(
    `%c[checkout] provider=${provider} scope=${scope} → ${fn}`,
    provider === "onvopay"
      ? "color:#fff;background:#7c3aed;padding:2px 6px;border-radius:4px;font-weight:600"
      : "color:#fff;background:#635bff;padding:2px 6px;border-radius:4px;font-weight:600",
  );
  return provider;
}

export const PAYMENT_ENVIRONMENT: "sandbox" | "live" = "sandbox";
